import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import StartTimeIcon from '@mui/icons-material/Timer';
import EndTimeIcon from '@mui/icons-material/AlarmOn';
import { useMessages } from 'modules/intl';
import { TimeInputField } from 'modules/common/components/TimeInputField';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import {
  AddNoteFormChangeHandlers,
  SuggestionProps
} from 'modules/notes/addNote/hooks';
import { Timer, useTimerContext } from '../Timer';
import { NOTE_FIELDS } from '../../noteSchema';
import { useTimePickerProps } from './useTimePickerProps';

export const messagesKeys = ['note.startTime', 'note.endTime'];

const useStyles = makeStyles((theme) => ({
  duration: {
    marginLeft: theme.spacing(0.5)
  },
  container: {
    display: 'flex',
    marginLeft: theme.spacing(0.5)
  },
  adornedEnd: {
    padding: theme.spacing(0),
    margin: theme.spacing(0)
  },
  startEnd: {
    maxWidth: theme.spacing(20),
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    '& .MuiIconButton-root': {
      margin: theme.spacing(0)
    }
  },
  startEndTimeInput: {
    maxWidth: theme.spacing(20),
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    '& .MuiFormHelperText-contained': {
      margin: theme.spacing(0)
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: theme.spacing(0.5)
    }
  }
}));

type TimeInputProps = {
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined;
  formChangeHandlers: AddNoteFormChangeHandlers;
  showStartEndTime: boolean;
  resetTimeValue?: boolean | undefined;
  setResetTimeValue?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  suggestionProps: SuggestionProps;
};
const startTimeIcon = <StartTimeIcon color="action" />;
const endTimeIcon = <EndTimeIcon color="action" />;

const useTimeErrorProps = (
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined
) => {
  const startTimeErrorProps = useMemo(
    () =>
      errorMessage(NOTE_FIELDS.START_TIME)
        ? {
            error: true,
            helperText: errorMessage(NOTE_FIELDS.START_TIME),
            inputProps: { 'data-testid': 'startTime-input' }
          }
        : { inputProps: { 'data-testid': 'startTime-input' } },
    [errorMessage]
  );

  const endTimeErrorProps = useMemo(
    () =>
      errorMessage(NOTE_FIELDS.END_TIME)
        ? {
            error: true,
            helperText: errorMessage(NOTE_FIELDS.END_TIME),
            inputProps: { 'data-testid': 'endTime-input' }
          }
        : { inputProps: { 'data-testid': 'endTime-input' } },
    [errorMessage]
  );

  return { startTimeErrorProps, endTimeErrorProps };
};

const useValues = ({
  hasSuggestionStartTime,
  hasSuggestionEndTime,
  suggestionStartTime,
  suggestionEndTime,

  resetTimeValue,
  startTime,
  endTime
}: {
  hasSuggestionStartTime?: boolean;
  hasSuggestionEndTime?: boolean;
  suggestionEndTime?: string | null;
  suggestionStartTime?: string | null;
  resetTimeValue?: boolean;
  startTime?: Date | null | undefined;
  endTime?: Date | null | undefined;
}) => {
  const startTimeValue = useMemo(() => {
    if (resetTimeValue) return null;

    if (!hasSuggestionStartTime && startTime)
      return DateTime.fromJSDate(startTime);
    else if (suggestionStartTime && hasSuggestionStartTime)
      return DateTime.fromISO(suggestionStartTime);
    else return null;
  }, [hasSuggestionStartTime, resetTimeValue, startTime, suggestionStartTime]);

  const endTimeValue = useMemo(() => {
    if (resetTimeValue) return null;

    if (!hasSuggestionEndTime && endTime) return DateTime.fromJSDate(endTime);
    else if (suggestionEndTime && hasSuggestionEndTime)
      return DateTime.fromISO(suggestionEndTime);
    else return null;
  }, [endTime, hasSuggestionEndTime, resetTimeValue, suggestionEndTime]);

  return { startTimeValue, endTimeValue };
};

export const TimeInput: React.FC<TimeInputProps> = ({
  errorMessage,
  formChangeHandlers: {
    handleDuration,
    handleEndTime,
    handleStartTime,
    resetDuration,
    resetTimeRange
  },
  showStartEndTime,
  resetTimeValue,
  setResetTimeValue,
  suggestionProps: {
    setHasSuggestionStartTime,
    setHasSuggestionDuration,
    setHasSuggestionEndTime,
    hasSuggestionStartTime,
    hasSuggestionDuration,
    hasSuggestionEndTime
  }
}: TimeInputProps) => {
  const { control } = useFormContext();
  const startTime = useWatch({
    control,
    name: 'startTime'
  });

  const endTime = useWatch({
    control,
    name: 'endTime'
  });

  const duration = useWatch({
    control,
    name: 'duration'
  });
  const classes = useStyles();

  const messages = useMessages(messagesKeys);
  const timePickerProps = useTimePickerProps();
  const { labelSuggestions } = useTimesheetLabelSuggestionContext();

  const durationField = useMemo(() => {
    return duration
      ? duration
      : (labelSuggestions?.labelSuggestions.duration as number);
  }, [duration, labelSuggestions?.labelSuggestions.duration]);
  const suggestionStartTime = useMemo(() => {
    if (labelSuggestions?.labelSuggestions.startTime) {
      return labelSuggestions?.labelSuggestions.startTime;
    }
  }, [labelSuggestions?.labelSuggestions.startTime]);

  const suggestionEndTime = useMemo(() => {
    if (labelSuggestions?.labelSuggestions.endTime)
      return labelSuggestions?.labelSuggestions.endTime;
  }, [labelSuggestions?.labelSuggestions.endTime]);

  const { startTimeErrorProps, endTimeErrorProps } = useTimeErrorProps(
    errorMessage
  );
  const { startTimeValue, endTimeValue } = useValues({
    hasSuggestionStartTime,
    hasSuggestionEndTime,
    suggestionStartTime,
    suggestionEndTime,
    resetTimeValue,
    startTime,
    endTime
  });

  const { running } = useTimerContext();
  const suggestionProps = useMemo(
    () => ({
      setHasSuggestionStartTime: setHasSuggestionStartTime,
      setHasSuggestionDuration: setHasSuggestionDuration,
      setHasSuggestionEndTime: setHasSuggestionEndTime,
      hasSuggestionDuration: hasSuggestionDuration
    }),
    [
      hasSuggestionDuration,
      setHasSuggestionDuration,
      setHasSuggestionEndTime,
      setHasSuggestionStartTime
    ]
  );

  return (
    <div data-testid="time-input-container" className={classes.container}>
      {showStartEndTime && (
        <>
          <TimeInputField
            testId="add-note-startTime"
            name={NOTE_FIELDS.START_TIME}
            label={messages['note.startTime']}
            clockMode={timePickerProps.ampm ? 12 : 24}
            value={startTimeValue}
            onChange={handleStartTime}
            className={classes.startEndTimeInput}
            icon={startTimeIcon}
            disabled={running}
            setResetTimeValue={setResetTimeValue}
            suggestionProps={suggestionProps}
            {...startTimeErrorProps}
          />
          <TimeInputField
            testId="add-note-endTime"
            name={NOTE_FIELDS.END_TIME}
            isEndTime={true}
            label={messages['note.endTime']}
            clockMode={timePickerProps.ampm ? 12 : 24}
            value={endTimeValue}
            onChange={handleEndTime}
            className={classes.startEndTimeInput}
            icon={endTimeIcon}
            disabled={running}
            setResetTimeValue={setResetTimeValue}
            suggestionProps={suggestionProps}
            {...endTimeErrorProps}
          />
        </>
      )}
      <Timer
        className={classes.duration}
        data-testid="add-note-timer"
        testId={'add-note-duration'}
        duration={durationField}
        resetTimeRange={resetTimeRange}
        resetDuration={resetDuration}
        handleDuration={handleDuration}
        showStartEndTime={showStartEndTime}
        resetTimeValue={resetTimeValue}
        setResetTimeValue={setResetTimeValue}
        suggestionProps={suggestionProps}
      />
    </div>
  );
};

export default TimeInput;
